import {
    Activity,
    AppWindow,
    Building,
    ChartColumn,
    Coins,
    Dumbbell,
    Flag,
    MailWarning,
    MapPin,
    MapPinCheck,
    Rss,
    Ticket,
    Trophy,
    UserPlus,
    Users as UsersIcon,
    UsersRound,
    Wrench,
    type LucideIcon
} from 'lucide-react';
import { bind } from 'lodash-decorators';
import { computed, makeObservable, observable } from 'mobx';
import React from 'react';
import { PartialRouteObject } from 'react-router';
import { component } from 'tsdi';
import AppStore from '@src/app-store';
import AppStoreApp from '@src/app-store/app';
import { CommunityList } from '@src/communities';
import DeadLetterQueues from '@src/dead-letter-queues';
import { FeedPlanning } from '@src/feed-planning';
import { CreateCommunity } from '@src/communities/community/create/create-community';
import CommunityDetails from '@src/communities/community/details/community-details';
import CommunityFeed from '@src/communities/community/feed';
import EditCommunityForm from '@src/communities/community/edit/edit-community-form';
import CommunityTeams from '@src/communities/community/teams';
import { CreateTeam } from '@src/communities/community/teams/create/create-team';
import { BulkCreateTeams } from '@src/communities/community/teams/bulk-create/bulk-create-teams';
import TeamDetails from '@src/teams/team/details';
import TeamFeed from '@src/teams/team/feed';
import TeamMembers from '@src/teams/team/members';
import { FacilityCreate } from '../facility/create';
import { FacilityEdit } from '../facility/edit';
import FacilityDetailsForm from '../facility/edit/facility-details-form';
import FacilityLocation from '../facility/edit/facility-location';
import { Facility } from '../facility/index';
import { FacilityList } from '../facility/list';
import FitnessServices from '../fitness-services';
import { Login } from '../login';
import { NoMatch } from '../no-match/index';
import PrivateWorkoutsVerification from '../private-workout-verification/list';
import Reports from '../reports';
import Tenant from '../tenant';
import { TenantOnboarding } from '../tenant-onboarding';
import TenantCommunity from '../tenant/community';
import TenantContract from '../tenant/contract';
import TenantDetail from '../tenant/detail';
import TenantEmployee from '../tenant/employee';
import EmployeeFitnessActivities from '../tenant/employee/activities';
import EmployeeActivityPoints from '../tenant/employee/activity-points';
import EmployeeDetail from '../tenant/employee/detail';
import EmployeeFacilities from '../tenant/employee/facilities';
import TenantEmployees from '../tenant/employees';
import TenantFeatures from '../tenant/features';
import TenantUsers from '../tenant/users';
import { TenantList } from '../tenants';
import Users from '../users';
import User from '../users/user';
import UserActivities from '../users/user/activities';
import UserActivityPoints from '../users/user/activity-points';
import UserDetails from '../users/user/detail';
import UserWallet from '../users/user/wallet';
import AccountOperationDetails from '../users/user/wallet/operation-details';

import FlagsTool from '../users/user/flags-tool';
import GpxExplorer from '../users/user/gpx-explorer';
import UserBadges from '../users/user/badges';
import UserGoals from '../users/user/goals';
import UserFitnessApps from '../users/user/fitness-apps';
import UserDevices from '../users/user/devices';
import { VoucherVendors } from '../vouchers';
import { VendorDetails } from '../vouchers/vendor-details';
import { VendorDetailsTab } from '../vouchers/vendor-details/details';
import { VendorProducts } from '../vouchers/vendor-details/products';
import VendorList from '../vouchers/vendor-list';
import EditVendorForm from '../vouchers/vendor-edit/edit-vendor-form';
import CreateVendorForm from '../vouchers/vendor-create/create-vendor-form';
import { ProductDetails } from '../vouchers/product-details';
import EditProductForm from '../vouchers/product-edit/edit-product-form';
import VoucherPurchases from '../vouchers/purchases';
import PurchaseEventDlq from '../vouchers/purchase-event-dlq';
import { WorkoutRules } from '../workout-rules';
import { Api, Permission } from './api';
import { injectTSDI } from './tsdi';
import { CommunityGoalsSection } from '@src/communities/community/goals';
import CommunitySettingsSection from '@src/communities/community/details/community-settings-section';
import Community from '@src/communities/community';
import Challenges from '@src/communities/community/challenges';
import ChallengeEditForm from '@src/communities/community/challenges/challenge-form/edit';
import ChallengeCreateForm from '@src/communities/community/challenges/challenge-form/create';
import ChallengeLeaderboard from '@src/communities/community/challenges/leaderboard';
import ChallengeList from '@src/challenges';
import CommunityPointDefinitions from '@src/communities/community/point-definitions';
import CreatePointDefinitionForm from '@src/communities/community/point-definitions/create';
import EditPointDefinitionForm from '@src/communities/community/point-definitions/edit';
import EditCommunitySettingsForm from '@src/communities/community/edit-settings/edit-community-settings-form';
import EditCommunityLeaderboardSettingsForm from '@src/communities/community/edit-leaderboard-settings/edit-community-leaderboard-settings-form';
import PreviewLeaderboards from '@src/communities/community/preview-leaderboards';
import CommunityMembers from '@src/communities/community/members';
import CommunityFacilityRestrictions from '@src/communities/community/facility-restrictions';
import CommunityLocations from '@src/communities/community/locations';
import CommunityBudget from '@src/communities/community/budget';
import CommunityWhitelabel from '@src/communities/community/whitelabel';
import { WorkoutRulesGlobalRules } from '@src/workout-rules/global-rules';
import { WorkoutRulesActivityPointsRules } from '@src/workout-rules/activity-points-rules';
import ActivityPointsRuleDetails from '@src/workout-rules/activity-points-rules/details';
import { CreateActivityPointsRule } from '@src/workout-rules/activity-points-rules/create-activity-points-rule';
import { EditActivityPointsRule } from '@src/workout-rules/activity-points-rules/edit-activity-points-rule';
import { WorkoutValidationRules } from '@src/workout-rules/workout-rules';
import { CreateWorkoutTypeRule } from '@src/workout-rules/workout-rules/create-workout-type-rule';
import { EditWorkoutTypeRule } from '@src/workout-rules/workout-rules/edit-workout-type-rule';
import { WorkoutTypeMappings } from '@src/workout-rules/workout-type-mapping';
import { CalculatedMetricDefinitions } from '@src/workout-rules/calculated-metrics';
import { CreateCalculatedMetricDefinition } from '@src/workout-rules/calculated-metrics/create-calculated-metric-definition';
import { EditCalculatedMetricDefinition } from '@src/workout-rules/calculated-metrics/edit-calculated-metric-definition';
import Team from '@src/teams/team';

import { SmartConnectPage } from '../onboarding/smart-connect/smart-connect-page';
import Badges from '../badges';
import Currencies from '../currencies';
import { Dashboard } from '../dashboard';

export type NavGroup = 'People' | 'Sport' | 'Catalog & Ops';

export interface Route extends PartialRouteObject {
    permissions?: Permission | Permission[];
    showInNav?: boolean | 'exact';
    element: React.ReactNode;

    Icon?: LucideIcon;
    navGroup?: NavGroup;

    children?: Route[];
    title?: string;
    path: string;
}

function flattenRoutes(
    routes: Route[],
    parentPath = ''
): { segments: string[]; title?: string }[] {
    return routes.flatMap((route) => {
        const fullPath = `${parentPath}/${route.path}`.replace(/\/+/g, '/');
        const entry = {
            segments: fullPath.split('/').filter(Boolean),
            title: route.title
        };
        return [entry, ...flattenRoutes(route.children ?? [], fullPath)];
    });
}

@component
export class Routes {
    public routes: Route[] = [];

    private get api() {
        return injectTSDI(Api);
    }

    @bind
    public getPageTitle(path: string): string | undefined {
        const name = path.match('/[a-zA-Z-]*');
        if (name) {
            const current = this.routes?.find(
                (route) => route.path === name[0]
            );
            return current && current.title;
        }
        return undefined;
    }

    constructor() {
        makeObservable(this, {
            routes: observable,
            activeRoutes: computed,
            navLinks: computed
        });

        this.routes = [
            {
                path: '/dashboard',
                element: <Dashboard />,
                Icon: Activity,
                navGroup: 'People',
                showInNav: true,
                permissions: ['LOGGED_IN'],
                title: 'Daily active users'
            },
            {
                path: '/users',
                element: <Users />,
                Icon: UsersIcon,
                navGroup: 'People',
                showInNav: true,
                permissions: ['LOGGED_IN'],
                title: 'Users'
            },
            {
                path: '/users/:userId',
                element: <User />,
                showInNav: false,
                permissions: ['LOGGED_IN'],
                title: 'User Details',
                children: [
                    {
                        path: '/',
                        element: <UserDetails />,
                        title: 'User Details'
                    },
                    {
                        path: '/details',
                        element: <UserDetails />,
                        title: 'User Details'
                    },
                    {
                        path: '/wallet',
                        element: <UserWallet />,
                        title: 'Wallet'
                    },
                    {
                        path: '/wallet/operations/:operationId',
                        element: <AccountOperationDetails />,
                        title: 'Operation Details'
                    },
                    {
                        path: '/activities',
                        element: <UserActivities />,
                        title: 'Employee Activities'
                    },
                    {
                        path: '/activity-points',
                        element: <UserActivityPoints />,
                        title: 'Activity Points'
                    },
                    {
                        path: '/badges',
                        element: <UserBadges />,
                        title: 'User Badges'
                    },
                    {
                        path: '/goals',
                        element: <UserGoals />,
                        title: 'User Goals'
                    },
                    {
                        path: '/flags-tool',
                        element: <FlagsTool />,
                        title: 'Fraud Monitor'
                    },
                    {
                        path: '/gpx-explorer',
                        element: <GpxExplorer />,
                        title: 'GPX Explorer'
                    },
                    {
                        path: '/fitness-apps',
                        element: <UserFitnessApps />,
                        title: 'Fitness Apps'
                    },
                    {
                        path: '/devices',
                        element: <UserDevices />,
                        title: 'Devices'
                    }
                ]
            },
            {
                path: '/',
                element: <Facility />,
                title: 'Dashboard',
                permissions: ['LOGGED_IN'],
                children: [
                    {
                        path: '/',
                        element: <TenantList />,
                        title: 'List'
                    }
                ]
            },
            {
                path: '/login',
                element: <Login />,
                title: 'Login'
            },
            {
                path: '/communities',
                element: <CommunityList />,
                Icon: UsersRound,
                navGroup: 'Sport',
                title: 'Communities',
                permissions: ['LOGGED_IN'],
                showInNav: true
            },
            {
                path: '/communities/create',
                element: <CreateCommunity />,
                title: 'Create Community',
                permissions: ['LOGGED_IN'],
                showInNav: false
            },
            {
                path: '/communities/:communityId',
                element: <Community />,
                title: 'Community Details',
                permissions: ['LOGGED_IN'],
                showInNav: false,
                children: [
                    {
                        path: '/',
                        element: <CommunityDetails />,
                        title: 'Community Details'
                    },
                    {
                        path: '/details',
                        element: <CommunityDetails />,
                        title: 'Community Details'
                    },
                    {
                        path: '/feed',
                        element: <CommunityFeed />,
                        title: 'Community Feed'
                    },
                    {
                        path: '/goals',
                        element: <CommunityGoalsSection />,
                        title: 'Community Goals'
                    },
                    {
                        path: '/challenges',
                        element: <Challenges />,
                        title: 'Community Challenges'
                    },
                    {
                        path: '/settings',
                        element: <CommunitySettingsSection />,
                        title: 'Community Settings'
                    },
                    {
                        path: '/whitelabel',
                        element: <CommunityWhitelabel />,
                        title: 'Whitelabel'
                    },
                    {
                        path: '/leaderboard-preview',
                        element: <PreviewLeaderboards />,
                        title: 'Leaderboards'
                    },
                    {
                        path: '/members',
                        element: <CommunityMembers />,
                        title: 'Community Members'
                    },
                    {
                        path: '/teams',
                        element: <CommunityTeams />,
                        title: 'Community Teams'
                    },
                    {
                        path: '/teams/create',
                        element: <CreateTeam />,
                        title: 'Create Team'
                    },
                    {
                        path: '/teams/bulk-create',
                        element: <BulkCreateTeams />,
                        title: 'Create Multiple Teams'
                    },
                    {
                        path: '/challenges/:challengeId/edit',
                        element: <ChallengeEditForm />,
                        title: 'Community Challenge Edit Form'
                    },
                    {
                        path: '/challenges/create',
                        element: <ChallengeCreateForm />,
                        title: 'Community Challenge Create Form'
                    },
                    {
                        path: '/point-definitions',
                        element: <CommunityPointDefinitions />,
                        title: 'Community Point Definitions'
                    },
                    {
                        path: '/point-definitions/create',
                        element: <CreatePointDefinitionForm />,
                        title: 'Create Point Definition'
                    },
                    {
                        path: '/point-definitions/:definitionId/edit',
                        element: <EditPointDefinitionForm />,
                        title: 'Edit Point Definition'
                    },
                    {
                        path: '/facility-restrictions',
                        element: <CommunityFacilityRestrictions />,
                        title: 'Facility Restrictions'
                    },
                    {
                        path: '/locations',
                        element: <CommunityLocations />,
                        title: 'Locations'
                    },
                    {
                        path: '/budget',
                        element: <CommunityBudget />,
                        title: 'Community Budget'
                    }
                ]
            },
            {
                path: '/communities/:communityId/edit',
                element: <EditCommunityForm />,
                title: 'Edit Community',
                permissions: ['LOGGED_IN'],
                showInNav: false
            },
            {
                path: '/communities/:communityId/challenges/:challengeId/leaderboard',
                element: <ChallengeLeaderboard />,
                title: 'Community Challenge Leaderboard',
                permissions: ['LOGGED_IN'],
                showInNav: false
            },
            {
                path: '/challenges',
                element: <ChallengeList />,
                Icon: Flag,
                navGroup: 'Sport',
                title: 'Challenges',
                permissions: ['LOGGED_IN'],
                showInNav: true
            },
            {
                path: '/challenges/:challengeId/leaderboard',
                element: <ChallengeLeaderboard />,
                title: 'Challenge Leaderboard',
                permissions: ['LOGGED_IN'],
                showInNav: false
            },
            {
                path: '/communities/:communityId/edit-settings',
                element: <EditCommunitySettingsForm />,
                title: 'Edit Community Settings',
                permissions: ['LOGGED_IN'],
                showInNav: false
            },
            {
                path: '/communities/:communityId/edit-leaderboard-settings',
                element: <EditCommunityLeaderboardSettingsForm />,
                title: 'Edit Community Leaderboard Settings',
                permissions: ['LOGGED_IN'],
                showInNav: false
            },
            {
                path: '/teams/:teamId',
                element: <Team />,
                title: 'Team Details',
                permissions: ['LOGGED_IN'],
                showInNav: false,
                children: [
                    {
                        path: '/',
                        element: <TeamDetails />,
                        title: 'Team Details'
                    },
                    {
                        path: '/details',
                        element: <TeamDetails />,
                        title: 'Team Details'
                    },
                    {
                        path: '/feed',
                        element: <TeamFeed />,
                        title: 'Team Feed'
                    },
                    {
                        path: '/members',
                        element: <TeamMembers />,
                        title: 'Team Members'
                    }
                ]
            },

            {
                path: '/tenants',
                element: <TenantList />,
                Icon: Building,
                navGroup: 'People',
                title: 'Tenants (Teams)',
                permissions: ['LOGGED_IN'],
                showInNav: true
            },
            {
                path: '/tenant/:tenantName',
                element: <Tenant />,
                title: 'Tenant Detail',
                showInNav: false,
                permissions: ['LOGGED_IN'],
                children: [
                    {
                        path: '/',
                        element: <TenantDetail />,
                        title: 'Details'
                    },
                    {
                        path: '/details',
                        element: <TenantDetail />,
                        title: 'Detail'
                    },
                    {
                        path: '/contract',
                        element: <TenantContract />,
                        title: 'Features'
                    },
                    {
                        path: '/features',
                        element: <TenantFeatures />,
                        title: 'Features'
                    },
                    {
                        path: '/employees',
                        element: <TenantEmployees />,
                        title: 'Employees'
                    },
                    {
                        path: '/community',
                        element: <TenantCommunity />,
                        title: 'Community'
                    },
                    {
                        path: '/employees/:employeeId',
                        element: <TenantEmployee />,
                        title: 'Features',
                        children: [
                            {
                                path: '/',
                                element: <EmployeeDetail />,
                                title: 'Employee Details'
                            },
                            {
                                path: '/details',
                                element: <EmployeeDetail />,
                                title: 'Employee Details'
                            },
                            {
                                path: '/facilities',
                                element: <EmployeeFacilities />,
                                title: 'Employee Facilities'
                            },
                            {
                                path: '/activities',
                                element: <EmployeeFitnessActivities />,
                                title: 'Employee Activities'
                            },
                            {
                                path: '/activity-points',
                                element: <EmployeeActivityPoints />,
                                title: 'Employee Activity Points'
                            }
                        ]
                    },
                    {
                        path: '/users',
                        element: <TenantUsers />,
                        title: 'Users'
                    }
                ]
            },
            {
                path: '/tenant-onboarding',
                element: <TenantOnboarding />,
                title: 'Tenant Teams Onboarding',
                permissions: ['LOGGED_IN']
            },
            {
                path: '/facility',
                element: <Facility />,
                Icon: MapPin,
                navGroup: 'Sport',
                title: 'Facilities',
                showInNav: true,
                permissions: ['LOGGED_IN'],
                children: [
                    {
                        path: '/',
                        element: <FacilityList />,
                        title: 'Facility List'
                    },
                    {
                        path: '/create',
                        element: <FacilityCreate />,
                        title: 'Facility Create'
                    },
                    {
                        path: '/:facilityId',
                        element: <FacilityEdit />,
                        title: 'Facility Edit',
                        children: [
                            {
                                path: '/',
                                element: <FacilityDetailsForm />,
                                title: 'Facility Details form'
                            },
                            {
                                path: '/details',
                                element: <FacilityDetailsForm />,
                                title: 'Facility Details form'
                            },
                            {
                                path: '/location',
                                element: <FacilityLocation />,
                                title: 'Facility Location Edit'
                            }
                        ]
                    }
                ]
            },
            {
                path: '/workout-rules',
                element: <WorkoutRules />,
                Icon: Dumbbell,
                navGroup: 'Sport',
                title: 'Workout Rules',
                permissions: ['LOGGED_IN'],
                showInNav: true,
                children: [
                    {
                        path: '/',
                        element: <WorkoutRulesGlobalRules />,
                        title: 'Workout Rules'
                    },
                    {
                        path: '/global-rules',
                        element: <WorkoutRulesGlobalRules />,
                        title: 'Workout Rules Global Rules'
                    },
                    {
                        path: '/activity-points-rules',
                        element: <WorkoutRulesActivityPointsRules />,
                        title: 'AP Multipliers Groups'
                    },
                    {
                        path: '/activity-points-rules/create',
                        element: <CreateActivityPointsRule />,
                        title: 'Create AP Multiplier Group'
                    },
                    {
                        path: '/activity-points-rules/:ruleId/edit',
                        element: <EditActivityPointsRule />,
                        title: 'Edit AP multiplier group'
                    },
                    {
                        path: '/activity-points-rules/:ruleId',
                        element: <ActivityPointsRuleDetails />,
                        title: 'AP multiplier group details'
                    },
                    {
                        path: '/workout-type-rules',
                        element: <WorkoutValidationRules />,
                        title: 'Validation Rules'
                    },
                    {
                        path: '/workout-type-rules/create',
                        element: <CreateWorkoutTypeRule />,
                        title: 'Create Validation Rules'
                    },
                    {
                        path: '/workout-type-rules/:ruleId/edit',
                        element: <EditWorkoutTypeRule />,
                        title: 'Edit Validation Rule'
                    },
                    {
                        path: '/workout-type-mappings',
                        element: <WorkoutTypeMappings />,
                        title: 'Workout Type Mappings'
                    },
                    {
                        path: '/calculated-metrics',
                        element: <CalculatedMetricDefinitions />,
                        title: 'Calculated Metrics'
                    },
                    {
                        path: '/calculated-metrics/create',
                        element: <CreateCalculatedMetricDefinition />,
                        title: 'Create Calculated Metric'
                    },
                    {
                        path: '/calculated-metrics/:definitionId/edit',
                        element: <EditCalculatedMetricDefinition />,
                        title: 'Edit Calculated Metric'
                    }
                ]
            },
            {
                path: '/fitness-services',
                element: <FitnessServices />,
                Icon: Wrench,
                navGroup: 'Sport',
                title: 'Fitness Services',
                permissions: ['LOGGED_IN'],
                showInNav: true
            },
            {
                path: '/reports',
                element: <Reports />,
                Icon: ChartColumn,
                navGroup: 'Catalog & Ops',
                showInNav: true,
                title: 'Reports'
            },
            {
                path: '/private-workout-verification',
                element: <PrivateWorkoutsVerification />,
                Icon: MapPinCheck,
                navGroup: 'Sport',
                showInNav: true,
                title: 'Private Workouts'
            },
            {
                path: '/feed-planning',
                element: <FeedPlanning />,
                Icon: Rss,
                navGroup: 'Catalog & Ops',
                title: 'Feed planning',
                permissions: ['LOGGED_IN'],
                showInNav: true
            },

            {
                path: '/vendors',
                element: <VoucherVendors />,
                Icon: Ticket,
                navGroup: 'Catalog & Ops',
                showInNav: true,
                permissions: ['LOGGED_IN'],
                title: 'Vouchers shop',
                children: [
                    {
                        path: '/',
                        element: <VendorList />,
                        title: 'Vendor List'
                    },
                    {
                        path: '/purchases',
                        element: <VoucherPurchases />,
                        title: 'Voucher Purchases'
                    },
                    {
                        path: '/create',
                        element: <CreateVendorForm />,
                        title: 'Create Vendor'
                    },
                    {
                        path: '/:vendorId',
                        element: <VendorDetails />,
                        title: 'Vendor details',
                        children: [
                            {
                                path: '/',
                                element: <VendorDetailsTab />,
                                title: 'Vendor Details'
                            },
                            {
                                path: '/details',
                                element: <VendorDetailsTab />,
                                title: 'Vendor Details'
                            },
                            {
                                path: '/products',
                                element: <VendorProducts />,
                                title: 'Vendor Products'
                            }
                        ]
                    },
                    {
                        path: '/:vendorId/edit',
                        element: <EditVendorForm />,
                        title: 'Edit Vendor'
                    },
                    {
                        path: '/:vendorId/products/:productId',
                        element: <ProductDetails />,
                        title: 'Product Details',
                        showInNav: false
                    },
                    {
                        path: '/:vendorId/products/:productId/edit',
                        element: <EditProductForm />,
                        title: 'Edit Product',
                        showInNav: false
                    }
                ]
            },

            {
                path: '/onboarding/smart-connect',
                element: <SmartConnectPage />,
                Icon: UserPlus,
                navGroup: 'People',
                title: 'Onboarding',
                permissions: ['LOGGED_IN'],
                showInNav: true
            },
            {
                path: '/badges',
                element: <Badges />,
                Icon: Trophy,
                navGroup: 'Catalog & Ops',
                title: 'Badges',
                permissions: ['LOGGED_IN'],
                showInNav: true
            },
            {
                path: '/currencies',
                element: <Currencies />,
                Icon: Coins,
                navGroup: 'Catalog & Ops',
                title: 'Currencies',
                permissions: ['LOGGED_IN'],
                showInNav: true
            },
            {
                path: '/app-store',
                element: <AppStore />,
                Icon: AppWindow,
                navGroup: 'Catalog & Ops',
                title: 'App Store',
                permissions: ['LOGGED_IN'],
                showInNav: true
            },
            {
                path: '/app-store/:app',
                element: <AppStoreApp />,
                title: 'App Store App',
                permissions: ['LOGGED_IN'],
                showInNav: false
            },
            {
                path: '/dead-letter-queues',
                element: <DeadLetterQueues />,
                Icon: MailWarning,
                navGroup: 'Catalog & Ops',
                title: 'Dead Letter Queues',
                permissions: ['LOGGED_IN'],
                showInNav: true
            },
            {
                path: '/purchase-event-dlq',
                element: <PurchaseEventDlq />,
                title: 'Purchase Event DLQ',
                permissions: ['LOGGED_IN'],
                showInNav: false
            },
            { path: '*', element: <NoMatch /> }
        ];
    }

    public get activeRoutes(): Route[] {
        return this.routes.filter((route) =>
            route.permissions ? this.api.hasPermission(route.permissions) : true
        );
    }

    @bind
    public getBreadcrumbs(pathname: string): { title: string; path: string }[] {
        const segments = pathname.split('/').filter(Boolean);
        const flat = flattenRoutes(this.routes);
        const crumbs: { title: string; path: string }[] = [];
        for (let length = 1; length <= segments.length; length++) {
            const prefix = segments.slice(0, length);
            const match = flat.find(
                (route) =>
                    route.segments.length === length &&
                    route.segments.every(
                        (segment, i) =>
                            segment === prefix[i] || segment.startsWith(':')
                    )
            );
            if (
                match?.title &&
                match.title !== crumbs[crumbs.length - 1]?.title
            ) {
                crumbs.push({
                    title: match.title,
                    path: `/${prefix.join('/')}`
                });
            }
        }
        return crumbs;
    }

    public get navLinks(): Route[] {
        return this.routes.filter((route) => route.showInNav);
    }
}
